// Nom des documents produits (PDF imprimé, DOCX exporté), des DEUX côtés.
//
// POURQUOI ICI. Le PDF n'est pas généré par nous : le bouton « Télécharger PDF »
// appelle `contentWindow.print()` sur l'iframe d'aperçu, et c'est **Chrome** qui
// propose le nom du fichier, dérivé du `<title>` du document imprimé. Le nom du
// DOCX, lui, est choisi par notre code. Deux chemins totalement différents, un
// seul nom attendu par l'utilisateur : d'où une fonction unique, dans le moteur,
// consommée par le rendu HTML (le `<title>`), par les deux exports Word, et par
// l'extension comme par la console.
//
// Les laisser diverger, c'est ce qui s'était produit : `CV_Adapte_{jobTitle}.docx`
// d'un côté, le nom du candidat seul (voire « about:blank ») de l'autre.
//
// CE QUE ÇA NE FAIT PAS : deviner. Un segment inconnu est OMIS, jamais remplacé
// par un `undefined` ni par un `__` — `nicolas_breels_cv` est un nom correct
// quand l'entreprise n'a pas pu être lue. C'est le pendant de la règle
// d'extraction : sur un site inconnu on ne devine pas l'employeur au CSS, on se
// tait (cf. `structuredJob.ts` et la plateforme `generic`).

/** Type de document produit. Sert de dernier segment du nom. */
export type DocumentKind = 'cv' | 'lettre'

/**
 * Suffixes NON traduits, volontairement.
 *
 * On vient pourtant de poser la règle inverse pour l'UI (ce que l'utilisateur
 * lit suit `uiLanguage`). Un nom de fichier est différent : il s'accumule dans
 * le dossier Téléchargements pendant des mois, et c'est là qu'on le relit six
 * semaines plus tard. Une bascule de langue d'interface couperait la collection
 * en deux conventions (`_lettre` d'avant, `_letter` d'après), sans que rien ne
 * relie les deux moitiés. La stabilité vaut mieux que la traduction ici — et
 * `cv` s'écrit de toute façon pareil dans les deux langues.
 */
const SUFFIXES: Record<DocumentKind, string> = { cv: 'cv', lettre: 'lettre' }

/**
 * Normalise un segment : minuscules, accents retirés, tout ce qui n'est pas
 * `[a-z0-9]` remplacé par `_`, pas de `_` consécutifs ni en bordure.
 *
 * `normalize('NFD')` sépare la lettre de son accent, la plage `̀-ͯ`
 * retire ensuite les diacritiques seuls : « Ștefan Müller » → `stefan_muller`.
 * Ce qu'aucune décomposition ne couvre (idéogrammes, cyrillique) tombe sur le
 * remplacement générique et peut donc rendre une chaîne VIDE — c'est traité
 * comme un segment absent par `documentBaseName`, pas comme une erreur.
 */
export function slugifySegment(raw: string | null | undefined): string {
  if (typeof raw !== 'string') return ''
  return raw
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

export interface DocumentNameParts {
  /** Nom complet du candidat (`basics.name` / signature de la lettre). */
  candidate?: string | null
  /** Employeur de l'offre. Absent = segment omis, jamais deviné. */
  company?: string | null
}

/**
 * Nom de base, SANS extension : `prenom_nom_entreprise_cv`.
 *
 * C'est aussi le `<title>` posé sur le document imprimé — Chrome en dérive le
 * nom du PDF, donc les deux ne peuvent pas diverger. Il n'y a pas de repli
 * « joli » : si tout manque, on rend `cv` / `lettre`, qui reste un nom valide.
 */
export function documentBaseName(parts: DocumentNameParts, kind: DocumentKind): string {
  return [slugifySegment(parts.candidate), slugifySegment(parts.company), SUFFIXES[kind]]
    .filter((segment) => segment.length > 0)
    .join('_')
}

/** Nom de fichier complet pour un export dont NOUS choisissons le nom (DOCX). */
export function documentFileName(
  parts: DocumentNameParts,
  kind: DocumentKind,
  extension: string
): string {
  return `${documentBaseName(parts, kind)}.${extension.replace(/^\./, '')}`
}
